import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loader from 'src/components/Loader';
import { useTranslation } from 'react-i18next';
import { ReportReviewBlock } from 'src/components/ReportReviewBlock';
import { getPreSignedUrl } from 'src/utils/helpers';
import s from './JobFormReportsTab.styles';

const JobFormReportsTab = ({
  job,
  isReportReviewer,
  isVendorUser,
  refetchReportReview,
  pdfAttachmentId,
  htmlAttachmentId,
  reportNames,
  notes,
}) => {
  const { i18n, t } = useTranslation();

  const jumpTo = name => {
    document.getElementById(`report-${name}`).scrollIntoView();
  };

  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState(null);

  useEffect(() => {
    if (!html) {
      return;
    }

    setTimeout(() => {
      const scripts = [
        ...html.matchAll(/<script[\s\S]*?>([\s\S]*?)<\/script>/gi),
      ];
      // eslint-disable-next-line no-eval
      scripts.forEach(script => window.eval(script[1]));
    }, 500);
  }, [html]);

  useEffect(() => {
    getPreSignedUrl(htmlAttachmentId).then(response => {
      fetch(response).then(async res => {
        setHtml(await res.text());
        setLoading(false);
      });
    });
  }, [htmlAttachmentId]);

  const onDownload = () => {
    getPreSignedUrl(pdfAttachmentId).then(response => {
      window.location.href = response;
    });
  };

  const [infoCollapsed, setInfoCollapsed] = useState(true);
  const [isCreateNote, setIsCreateNote] = useState(false);

  const isEditable = ['Finalized', 'Ready for cap'].includes(job.status);

  let wrapperHtml = html ? html.replace(/^([.a-z].+{)$/mg, '#report-wrapper $1') : null;
  wrapperHtml = wrapperHtml ? wrapperHtml.replaceAll('.w-100 > div, .w-100 > h2', '#report-wrapper .w-100 > div, #report-wrapper .w-100 > h2') : null;

  return (
    <s.ReportsWrapper>
      <s.Left>
        <h1>Reports</h1>
        <s.ButtonsRow>
          <div>
            <s.QuickJumpLabel>{t('QUICK_JUMP')}</s.QuickJumpLabel>
            {reportNames.map((reportName, index) => (
              <s.GreySwitchButton key={index} onClick={() => jumpTo(reportName)}>
                {reportName}
              </s.GreySwitchButton>
            ))}
          </div>
          <s.DownloadWrapper>
            <s.DownloadType>{t('FORMAT_PDF')}</s.DownloadType>
            <s.DownloadBtn onClick={() => onDownload()} type="button">
              <s.DownloadIcon name="download" />
              {t('GENERATE')}
            </s.DownloadBtn>
          </s.DownloadWrapper>
        </s.ButtonsRow>
        {loading ? (
          <s.Report>
            <Loader />
          </s.Report>
        ) : (
          <s.Report dangerouslySetInnerHTML={{ __html: wrapperHtml }} id="report-wrapper" />
        )}
      </s.Left>
      <s.Right>
        {(isReportReviewer || isVendorUser) && (
          <s.CollapsedBtn lang={i18n.language} onClick={() => setInfoCollapsed(false)}>
            {t('REVIEW_NOTES')}
          </s.CollapsedBtn>
        )}
      </s.Right>
      <s.InfoOverlay hidden={infoCollapsed}>
        <s.InfoOverlayContent>
          <s.CloseBtn name="cross" onClick={() => {
            setIsCreateNote(false);
            setInfoCollapsed(true);
          }} />
          <s.AddNoteButtonWithTitleDiv>
            <s.QuestionTitle>
              {t('REPORT_REVIEW_NOTES')}
            </s.QuestionTitle>

            {!isReportReviewer && isEditable && !isCreateNote && (
              <s.AddButtonWrapper onClick={() => setIsCreateNote(true)}>
                <s.AddButton name="plus" />
                <s.AddButtonLabel>{t('ADD_NOTE')}</s.AddButtonLabel>
              </s.AddButtonWrapper>
            )}
          </s.AddNoteButtonWithTitleDiv>

          {notes.map((note, index) => (
            <ReportReviewBlock
              key={index}
              activeReportReviewComments={note.reportReviewComments || []}
              activeRepotReviewNote={note}
              isCreateNote={false}
              isReportReviewer={isReportReviewer}
              isUnderReviewing={(isReportReviewer || (isVendorUser && !note.closedAt)) && isEditable}
              jobId={job.id}
              refetchReportReview={refetchReportReview}
              setIsCreateNote={() => {}}
              setShowNote={() => {}}
              setShowResult={() => {}}
              showNote={true}
              type="report"
              typeId={1}
              typeQuestionId={1}
              typeVersionId={1}
            />
          ))}
          {isCreateNote && (
            <ReportReviewBlock
              activeReportReviewComments={[]}
              activeRepotReviewNote={null}
              isCreateNote={isCreateNote}
              isReportReviewer={isReportReviewer}
              isUnderReviewing={isEditable}
              jobId={job.id}
              refetchReportReview={refetchReportReview}
              setIsCreateNote={setIsCreateNote}
              setShowNote={() => {}}
              setShowResult={() => {}}
              showNote={true}
              type="report"
              typeId={1}
              typeQuestionId={1}
              typeVersionId={1}
            />
          )}
        </s.InfoOverlayContent>
      </s.InfoOverlay>
    </s.ReportsWrapper>
  );
};

JobFormReportsTab.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.number,
    status: PropTypes.string,
  }),
  isReportReviewer: PropTypes.bool,
  isVendorUser: PropTypes.bool,
  refetchReportReview: PropTypes.func,
  pdfAttachmentId: PropTypes.number,
  htmlAttachmentId: PropTypes.number,
  reportNames: PropTypes.arrayOf(PropTypes.string),
  notes: PropTypes.arrayOf(PropTypes.shape({})),
};

export default React.memo(JobFormReportsTab);
